import React, { useState, useEffect } from 'react';
import { SearchIcon, CloseIcon } from './icons';

const QUERY_HISTORY_STORAGE_KEY = 'busin.us-query-history';

const QueryHistory = ({ onQuerySelect, isOpen, onClose, className = '' }) => {
  const [history, setHistory] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Load history from localStorage on component mount
  useEffect(() => {
    try {
      const savedHistory = localStorage.getItem(QUERY_HISTORY_STORAGE_KEY);
      if (savedHistory) {
        setHistory(JSON.parse(savedHistory));
      }
    } catch (e) {
      console.error('Failed to load query history from localStorage', e);
    }
  }, []);

  // Save history to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(QUERY_HISTORY_STORAGE_KEY, JSON.stringify(history));
    } catch (e) {
      console.error('Failed to save query history to localStorage', e);
    }
  }, [history]);

  // Add a new query to history
  const addQueryToHistory = (query) => {
    if (!query || typeof query !== 'string') return;
    
    const trimmedQuery = query.trim();
    if (!trimmedQuery) return;
    
    setHistory(prevHistory => {
      // Remove duplicates and add to the beginning
      const filteredHistory = prevHistory.filter(item => item.query !== trimmedQuery);
      const newHistory = [
        {
          id: Date.now(),
          query: trimmedQuery,
          timestamp: new Date().toISOString()
        },
        ...filteredHistory
      ];
      
      // Keep only the last 20 queries
      return newHistory.slice(0, 20);
    });
  };

  // Remove a query from history
  const removeQueryFromHistory = (id) => {
    setHistory(prevHistory => prevHistory.filter(item => item.id !== id));
  };

  // Clear all history
  const clearHistory = () => {
    setHistory([]);
  };

  // Filter history based on search term
  const filteredHistory = history.filter(item => 
    item.query.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className={`bg-dark-surface border border-dark-border rounded-lg shadow-lg ${className}`}>
      <div className="p-4 border-b border-dark-border flex items-center justify-between">
        <h3 className="text-lg font-semibold text-dark-text-primary">Query History</h3>
        <button 
          onClick={onClose}
          className="p-1 rounded-md text-dark-text-secondary hover:bg-dark-bg"
        >
          <CloseIcon className="w-5 h-5" />
        </button>
      </div>
      
      {history.length > 0 ? (
        <>
          <div className="p-4 border-b border-dark-border">
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-text-secondary" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search history..."
                className="w-full bg-dark-bg border border-dark-border rounded-md py-2 pl-10 pr-4 text-sm text-dark-text-primary focus:outline-none focus:ring-1 focus:ring-brand-primary"
              />
            </div>
          </div>
          
          <div className="max-h-60 overflow-y-auto">
            {filteredHistory.length > 0 ? (
              <ul className="divide-y divide-dark-border">
                {filteredHistory.map((item) => (
                  <li 
                    key={item.id} 
                    className="p-3 hover:bg-dark-bg cursor-pointer group flex items-center justify-between"
                    onClick={() => onQuerySelect(item.query)}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-dark-text-primary truncate">{item.query}</p>
                      <p className="text-xs text-dark-text-secondary mt-1">
                        {new Date(item.timestamp).toLocaleString()}
                      </p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeQueryFromHistory(item.id);
                      }}
                      className="p-1 rounded-md text-dark-text-secondary hover:bg-dark-border opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <CloseIcon className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="p-4 text-center text-dark-text-secondary">
                No matching queries found
              </div>
            )}
          </div>
          
          <div className="p-3 border-t border-dark-border flex justify-between items-center">
            <span className="text-sm text-dark-text-secondary">
              {history.length} {history.length === 1 ? 'query' : 'queries'}
            </span>
            <button
              onClick={clearHistory}
              className="text-sm text-red-400 hover:text-red-300"
            >
              Clear History
            </button>
          </div>
        </>
      ) : (
        <div className="p-8 text-center text-dark-text-secondary">
          <p>No query history yet</p>
          <p className="text-sm mt-2">Your generated queries will appear here</p>
        </div>
      )}
    </div>
  );
};

export default QueryHistory;
export { QUERY_HISTORY_STORAGE_KEY };